import React, {useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {gap, m, p} from "@pg-design/helpers-css";
import {borderRadius, mb, pr} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {gtmEventPhoneVerification} from "@pg-mono/google-tag-manager/src/gtm_event_phone_verification";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat} from "@pg-mono/string-utils";

import {useUserPhoneVerificationContext} from "../context/UserPhoneVerificationContext";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";
import {getTimeInMinutes} from "../utils/get_time_in_minutes";

const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;
const PHONE_VERIFICATION_TIMER_START_LS_KEY = "phoneVerificationTimerStart";

interface IProps {
    isOpen: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    onChangeNumber: () => void;
    phoneNumber?: string;
}

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        // Transform empty string or non-numeric strings to null
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object<IFormData>().shape({
    firstDigit: digitValidation.nullable(),
    secondDigit: digitValidation.nullable(),
    thirdDigit: digitValidation.nullable(),
    fourthDigit: digitValidation.nullable()
});

export const UserPhoneVerification = (props: IProps) => {
    const {isOpen, onClose, onSuccess, onChangeNumber, phoneNumber: propPhoneNumber} = props;
    const context = useUserPhoneVerificationContext();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(propPhoneNumber || null);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        const savedStartTime = lscache.get(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = context.hotlinePhoneNumber ? phoneNumberFormat(context.hotlinePhoneNumber) : "";

    // Fetch user profile to get phone number if not provided
    useEffect(() => {
        if (isOpen && !propPhoneNumber) {
            context.getUserProfile().then((profile) => {
                if (profile?.phone) {
                    setUserPhoneNumber(profile.phone);
                }
            });
        }
    }, [isOpen, propPhoneNumber, context]);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime]);

    useEffect(() => {
        if (isOpen && formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }

            prevIsOpenRef.current = isOpen;

            if (currentRemainingTime === 0 || !timerStartTime) {
                handlers.setNewStartTime();
                sendNewCode();
            } else {
                setTimer(currentRemainingTime);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [isOpen, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            if (onClose) {
                onClose();
            }
        },
        changeNumberButton: () => {
            onChangeNumber();
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(PHONE_VERIFICATION_TIMER_START_LS_KEY, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            form.setValue(fieldName, numericValue ? parseInt(numericValue.slice(-1)) : null);

            // Move to next input if value is entered
            if (numericValue && numericValue.length > 0) {
                digitNavigation.moveToNextField(fieldName);
            }
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.sendPhoneVerificationCode();

            if (response.error) {
                const error = response.error;

                if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
                    // Clear timer and localStorage for 429 error status
                    handlers.clearStartTime();
                    setTimer(0);
                }

                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas wysyłania kodu", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values: IFormData) => {
        const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.verifyPhone(phoneVerificationCode);

            if (response.error) {
                setFormError(response.error);
                if (context.onError) {
                    context.onError(response.error);
                }
            } else {
                gtmEventPhoneVerification("submitted");
                onSuccess?.();
                lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas weryfikacji", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;

            // Check if all digits are filled and valid
            if (firstDigit !== null && secondDigit !== null && thirdDigit !== null && fourthDigit !== null) {
                // Trigger form submission automatically
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit, formError]);

    if (!userPhoneNumber) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={handlers.onCloseButton} css={modalStyle}>
            <Modal.CloseButton
                placement="right"
                variant="rounded"
                css={css`
                    ${m(2)}
                `}
            />
            <Modal.Content>
                <Text
                    variant="headline_3"
                    align="left"
                    mb={1}
                    css={css`
                        ${pr(1)}
                    `}
                >
                    Zweryfikuj swój <Highlight>numer telefonu</Highlight>
                </Text>
                <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                    Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    <span css={boldTextStyle}>{phoneNumberFormat(userPhoneNumber)}</span>.{" "}
                    <span css={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                        Zmień numer
                    </span>
                </Text>
                <FormProviderRHF {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div css={formStyle}>
                            {digitFields.map((fieldName, index) => (
                                <InputField
                                    key={fieldName}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    name={fieldName}
                                    maxLength={1}
                                    css={inputStyle}
                                    inputMode="numeric"
                                    autoComplete={index === 0 ? "one-time-code" : "off"}
                                    onChange={(e) => handlers.inputFocusChange(fieldName, e.target.value)}
                                    onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                    onPaste={handlers.inputPaste}
                                />
                            ))}
                        </div>
                        {formError && (
                            <Text variant="info_txt_1" css={errorMessageStyle}>
                                {formError.message}
                            </Text>
                        )}
                        <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle}>
                            POTWIERDŹ
                        </Button>
                        {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS ? (
                            <Text variant="info_txt_1" as="div" css={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                                {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                            </Text>
                        ) : (
                            <Text variant="info_txt_1" css={timerStyle}>
                                Nie otrzymałeś kodu? <br />
                                {context.hotlinePhoneNumber && (
                                    <>
                                        Skontaktuj się z naszym biurem obsługi:{" "}
                                        <a
                                            css={[
                                                timerStyle,
                                                css`
                                                    font-weight: 700;
                                                `
                                            ]}
                                            href={`tel:+${hotlinePhoneFormat}`}
                                        >
                                            {phoneNumberFormat(context.hotlinePhoneNumber)}
                                        </a>
                                    </>
                                )}
                            </Text>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

//Styles
const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${p(2)};
        ${borderRadius(2)};
        width: 544px;
        height: 448px;
    }
`;

const formStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    ${gap(1)};
    margin: auto;
    ${mb(4)};
    height: 72px;
    width: 72px;
    justify-content: center;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(3)};
        height: 112px;
        width: 112px;
    }
`;

const buttonStyle = css`
    align-items: center;
    ${mb(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;

const changeNumberButtonStyle = css`
    text-decoration: underline;
    cursor: pointer;
`;

const inputStyle = (theme: Theme) => css`
    text-align: center;
    border-radius: 8px;
    border: 1px solid var(--colors-gray-600);
    font-size: 30.79px;

    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 35.92px;
    }

    & div {
        border: none;
    }

    & > div:first-child {
        height: 72px;
        width: 72px;

        @media (min-width: ${theme.breakpoints.sm}) {
            height: 112px;
            width: 112px;
        }

        & > div:first-child {
            height: 70px;
            width: 72px;

            @media (min-width: ${theme.breakpoints.sm}) {
                height: 110px;
                width: 112px;
            }
        }
    }

    & input {
        height: 100%;
        width: 100%;
        text-align: center;
        font-weight: 600;
    }

    & [data-name="field-message"] {
        display: flex;
        justify-content: center;
    }
`;

const timerStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
`;

const sendCodeInfoStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
    cursor: pointer;
    text-decoration: underline;
`;

const errorMessageStyle = css`
    color: var(--colors-danger);
    text-align: center;
    ${mb(2)};
`;
import React, {useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {gap, m, p} from "@pg-design/helpers-css";
import {borderRadius, mb, pr} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {gtmEventPhoneVerification} from "@pg-mono/google-tag-manager/src/gtm_event_phone_verification";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat} from "@pg-mono/string-utils";

import {useUserPhoneVerificationContext} from "../context/UserPhoneVerificationContext";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";
import {getTimeInMinutes} from "../utils/get_time_in_minutes";

const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;
const PHONE_VERIFICATION_TIMER_START_LS_KEY = "phoneVerificationTimerStart";

interface IProps {
    isOpen: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    onChangeNumber: () => void;
    phoneNumber?: string;
}

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        // Transform empty string or non-numeric strings to null
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object<IFormData>().shape({
    firstDigit: digitValidation.nullable(),
    secondDigit: digitValidation.nullable(),
    thirdDigit: digitValidation.nullable(),
    fourthDigit: digitValidation.nullable()
});

export const UserPhoneVerification = (props: IProps) => {
    const {isOpen, onClose, onSuccess, onChangeNumber, phoneNumber: propPhoneNumber} = props;
    const context = useUserPhoneVerificationContext();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(propPhoneNumber || null);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        const savedStartTime = lscache.get(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = context.hotlinePhoneNumber ? phoneNumberFormat(context.hotlinePhoneNumber) : "";

    // Fetch user profile to get phone number if not provided
    useEffect(() => {
        if (isOpen && !propPhoneNumber) {
            context.getUserProfile().then((profile) => {
                if (profile?.phone) {
                    setUserPhoneNumber(profile.phone);
                }
            });
        }
    }, [isOpen, propPhoneNumber, context]);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime]);

    useEffect(() => {
        if (isOpen && formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }

            prevIsOpenRef.current = isOpen;

            if (currentRemainingTime === 0 || !timerStartTime) {
                handlers.setNewStartTime();
                sendNewCode();
            } else {
                setTimer(currentRemainingTime);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [isOpen, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            if (onClose) {
                onClose();
            }
        },
        changeNumberButton: () => {
            onChangeNumber();
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(PHONE_VERIFICATION_TIMER_START_LS_KEY, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            form.setValue(fieldName, numericValue ? parseInt(numericValue.slice(-1)) : null);

            // Move to next input if value is entered
            if (numericValue && numericValue.length > 0) {
                digitNavigation.moveToNextField(fieldName);
            }
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.sendPhoneVerificationCode();

            if (response.error) {
                const error = response.error;

                if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
                    // Clear timer and localStorage for 429 error status
                    handlers.clearStartTime();
                    setTimer(0);
                }

                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas wysyłania kodu", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values: IFormData) => {
        const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.verifyPhone(phoneVerificationCode);

            if (response.error) {
                setFormError(response.error);
                if (context.onError) {
                    context.onError(response.error);
                }
            } else {
                gtmEventPhoneVerification("submitted");
                onSuccess?.();
                lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas weryfikacji", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;

            // Check if all digits are filled and valid
            if (firstDigit !== null && secondDigit !== null && thirdDigit !== null && fourthDigit !== null) {
                // Trigger form submission automatically
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit, formError]);

    if (!userPhoneNumber) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={handlers.onCloseButton} css={modalStyle}>
            <Modal.CloseButton
                placement="right"
                variant="rounded"
                css={css`
                    ${m(2)}
                `}
            />
            <Modal.Content>
                <Text
                    variant="headline_3"
                    align="left"
                    mb={1}
                    css={css`
                        ${pr(1)}
                    `}
                >
                    Zweryfikuj swój <Highlight>numer telefonu</Highlight>
                </Text>
                <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                    Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    <span css={boldTextStyle}>{phoneNumberFormat(userPhoneNumber)}</span>.{" "}
                    <span css={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                        Zmień numer
                    </span>
                </Text>
                <FormProviderRHF {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div css={formStyle}>
                            {digitFields.map((fieldName, index) => (
                                <InputField
                                    key={fieldName}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    name={fieldName}
                                    maxLength={1}
                                    css={inputStyle}
                                    inputMode="numeric"
                                    autoComplete={index === 0 ? "one-time-code" : "off"}
                                    onChange={(e) => handlers.inputFocusChange(fieldName, e.target.value)}
                                    onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                    onPaste={handlers.inputPaste}
                                />
                            ))}
                        </div>
                        {formError && (
                            <Text variant="info_txt_1" css={errorMessageStyle}>
                                {formError.message}
                            </Text>
                        )}
                        <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle}>
                            POTWIERDŹ
                        </Button>
                        {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS ? (
                            <Text variant="info_txt_1" as="div" css={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                                {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                            </Text>
                        ) : (
                            <Text variant="info_txt_1" css={timerStyle}>
                                Nie otrzymałeś kodu? <br />
                                {context.hotlinePhoneNumber && (
                                    <>
                                        Skontaktuj się z naszym biurem obsługi:{" "}
                                        <a
                                            css={[
                                                timerStyle,
                                                css`
                                                    font-weight: 700;
                                                `
                                            ]}
                                            href={`tel:+${hotlinePhoneFormat}`}
                                        >
                                            {phoneNumberFormat(context.hotlinePhoneNumber)}
                                        </a>
                                    </>
                                )}
                            </Text>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

//Styles
const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${p(2)};
        ${borderRadius(2)};
        width: 544px;
        height: 448px;
    }
`;

const formStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    ${gap(1)};
    margin: auto;
    ${mb(4)};
    height: 72px;
    width: 72px;
    justify-content: center;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(3)};
        height: 112px;
        width: 112px;
    }
`;

const buttonStyle = css`
    align-items: center;
    ${mb(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;

const changeNumberButtonStyle = css`
    text-decoration: underline;
    cursor: pointer;
`;

const inputStyle = (theme: Theme) => css`
    text-align: center;
    border-radius: 8px;
    border: 1px solid var(--colors-gray-600);
    font-size: 30.79px;

    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 35.92px;
    }

    & div {
        border: none;
    }

    & > div:first-child {
        height: 72px;
        width: 72px;

        @media (min-width: ${theme.breakpoints.sm}) {
            height: 112px;
            width: 112px;
        }

        & > div:first-child {
            height: 70px;
            width: 72px;

            @media (min-width: ${theme.breakpoints.sm}) {
                height: 110px;
                width: 112px;
            }
        }
    }

    & input {
        height: 100%;
        width: 100%;
        text-align: center;
        font-weight: 600;
    }

    & [data-name="field-message"] {
        display: flex;
        justify-content: center;
    }
`;

const timerStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
`;

const sendCodeInfoStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
    cursor: pointer;
    text-decoration: underline;
`;

const errorMessageStyle = css`
    color: var(--colors-danger);
    text-align: center;
    ${mb(2)};
`;
